import {
  useQuery,
  UseQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
  QueryKey,
} from '@tanstack/react-query';
import { FieldSectionApi } from 'api/field-sections';
import {
  CreateFieldSectionPayload,
  FieldSection,
  FieldSectionType,
  FieldSectionSubType,
} from 'api/field-sections/types';
import { ShiftSectionsPayload } from 'components/organisms/field-sections/modal';
import { useHasAccessToSkillMatrix } from 'hooks/use-skill-matrix-permission';
import { ALL_FIELDS_QUERY_KEY } from 'queries/constants';
import { SETTINGS_SECTION_FIELDS_QUERY_KEY } from 'utils/project-fields/use-settings-section-fields';

/* PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW will be renamed to PROJECT_FIELD_SECTIONS_QUERY_KEY after moving project-field-sections implementation to field-sections common implementation */
export const PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW = ['project-field-sections'];
export const TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY = ['user-field-sections'];
export const TASK_FIELD_SECTIONS_QUERY_KEY = ['task-field-sections'];
export const SPRINT_FIELD_SECTIONS_QUERY_KEY = ['sprint-field-sections'];
export const EPIC_FIELD_SECTIONS_QUERY_KEY = ['epic-field-sections'];
export const BACKLOG_FIELD_SECTIONS_QUERY_KEY = ['backlog-field-sections'];
export const SKILL_FIELD_SECTIONS_QUERY_KEY = ['skill-field-sections'];
export const COMPANY_FIELD_SECTIONS_QUERY_KEY = ['company-field-sections'];
export const CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY = [
  'customer-team-member-field-sections',
];
export const INVOICE_FIELD_SECTIONS_QUERY_KEY = ['invoice-field-sections'];
export const BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY = [
  'budget-financial-field-sections',
];
export const TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY = [
  'time-entry-field-sections',
];

export const EXPENSE_FIELD_SECTIONS_QUERY_KEY = ['expense-field-sections'];
export const ALLOCATION_FIELD_SECTIONS_QUERY_KEY = [
  'allocation-field-sections',
];

const queryKeyMap = new Map<FieldSectionType, QueryKey>([
  [FieldSectionType.Project, PROJECT_FIELD_SECTIONS_QUERY_KEY_NEW],
  [FieldSectionType.Task, TASK_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Users, TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Sprint, SPRINT_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Company, COMPANY_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Invoice, INVOICE_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Budget, BUDGET_FINANCIAL_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.TimeEntry, TIME_ENTRY_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.Expense, EXPENSE_FIELD_SECTIONS_QUERY_KEY],
  [FieldSectionType.ResourceAllocation, ALLOCATION_FIELD_SECTIONS_QUERY_KEY],
]);

export const getFieldSectionQueryKey = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubType?: FieldSectionSubType,
): QueryKey => {
  if (fieldSectionSubType === FieldSectionSubType.Skill) {
    return SKILL_FIELD_SECTIONS_QUERY_KEY;
  }

  if (fieldSectionSubType === FieldSectionSubType.ExternalUser) {
    return CUSTOMER_TEAM_MEMBER_FIELD_SECTIONS_QUERY_KEY;
  }

  return queryKeyMap.get(fieldSectionType)!;
};

export const useFieldSectionsQuery = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubtype = FieldSectionSubType.Default,
  enabled = true,
): UseQueryResult<FieldSection[]> => {
  const QUERY_KEY = getFieldSectionQueryKey(
    fieldSectionType,
    fieldSectionSubtype,
  );

  return useQuery({
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: QUERY_KEY,
    queryFn: () => {
      return FieldSectionApi.getAllFieldSections(
        fieldSectionType,
        fieldSectionSubtype,
      );
    },
    select: (fieldSections) => {
      return fieldSections.map((section, ind) => ({
        ...section,
        sectionOrder: ind,
      }));
    },
  });
};

export const useSkillFieldSectionsQuery = (
  enabled = true,
): UseQueryResult<FieldSection[]> => {
  const hasAccessToSkillMatrix = useHasAccessToSkillMatrix();

  return useFieldSectionsQuery(
    FieldSectionType.Users,
    FieldSectionSubType.Skill,
    enabled && hasAccessToSkillMatrix,
  );
};

export const deleteFieldSectionV2 = (
  fieldSection: FieldSection[],
  deleteFieldSectionId: number,
): FieldSection[] => {
  const updatedFieldSections: FieldSection[] = [];
  const deletedFieldSection = fieldSection.find(
    (section) => section.fieldSectionId === deleteFieldSectionId,
  );

  const deletedFieldSectionOrder = deletedFieldSection?.fieldOrder
    ? deletedFieldSection.fieldOrder
    : [];

  fieldSection.forEach((section) => {
    if (section !== deletedFieldSection) {
      updatedFieldSections.push({
        ...section,
        fieldOrder: section.default
          ? [...section.fieldOrder, ...deletedFieldSectionOrder] // Append the deleted section field order to default section
          : section.fieldOrder,
      });
    }
  });

  return updatedFieldSections;
};

export const useDeleteFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubType?: FieldSectionSubType,
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(
    fieldSectionType,
    fieldSectionSubType,
  );

  return useMutation<
    void,
    unknown,
    number,
    { previous: FieldSection[] | undefined }
  >((fieldSectionId) => FieldSectionApi.deleteFieldSection(fieldSectionId), {
    onMutate: async (fieldSectionId) => {
      await queryClient.cancelQueries(QUERY_KEY);
      const previousFieldSections =
        queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

      if (previousFieldSections) {
        const updatedFieldSections = deleteFieldSectionV2(
          previousFieldSections,
          fieldSectionId,
        );

        queryClient.setQueryData<FieldSection[]>(
          QUERY_KEY,
          updatedFieldSections,
        );
      }

      return { previous: previousFieldSections };
    },
    onError: (err, fieldSections, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previous);
    },
  });
};

interface UpdateFieldSectionOrderProps {
  updatedFieldSections: FieldSection[];
  fieldId: number;
  fieldSectionId: number;
  fieldOrderIndex: number;
}

export const useUpdateFieldSectionOrderMutationV2 = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubType?: FieldSectionSubType,
): UseMutationResult<FieldSection[], unknown, UpdateFieldSectionOrderProps> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(
    fieldSectionType,
    fieldSectionSubType,
  );

  return useMutation<
    FieldSection[],
    unknown,
    UpdateFieldSectionOrderProps,
    { previous: FieldSection[] | undefined }
  >(
    ({ fieldId, fieldSectionId, fieldOrderIndex }) =>
      FieldSectionApi.updateFieldSectionOrder({
        fieldId,
        fieldSectionId,
        fieldOrderIndex,
      }),
    {
      onMutate: async ({ updatedFieldSections }) => {
        const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

        queryClient.setQueryData(QUERY_KEY, updatedFieldSections);

        return { previous };
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(ALL_FIELDS_QUERY_KEY);

        await queryClient.invalidateQueries({
          queryKey: [SETTINGS_SECTION_FIELDS_QUERY_KEY],
        });
      },
      onError: (err, fieldSections, context) => {
        if (QUERY_KEY) {
          queryClient.setQueryData(QUERY_KEY, context?.previous);
        }
      },
    },
  );
};

export const useCreatingFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubType?: FieldSectionSubType,
): UseMutationResult<FieldSection, unknown, CreateFieldSectionPayload> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(
    fieldSectionType,
    fieldSectionSubType,
  );

  return useMutation<FieldSection, unknown, CreateFieldSectionPayload>(
    (createFieldSectionPayload) =>
      FieldSectionApi.createFieldSection(createFieldSectionPayload),
    {
      onSuccess: (data) => {
        if (QUERY_KEY) {
          const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

          if (previous) {
            queryClient.setQueryData(QUERY_KEY, [...previous, data]);
          }
        }
      },
    },
  );
};

type UpdateFieldSectionPayload = Pick<
  FieldSection,
  | 'fieldSectionId'
  | 'fieldSectionName'
  | 'fieldSectionType'
  | 'fieldSectionSubType'
  | 'private'
  | 'readUserGroupIds'
  | 'writeUserGroupIds'
>;

export const useShiftSectionsMutation = (
  fieldSectionType: FieldSectionType,
): UseMutationResult<unknown, unknown, ShiftSectionsPayload, unknown> => {
  return useMutation((shiftSectionspayload) =>
    FieldSectionApi.shiftSections(
      fieldSectionType,
      shiftSectionspayload.sectionOrderDetailsList,
    ),
  );
};

export const updateFieldSectionV2 = (
  fieldSections: FieldSection[],
  fieldSectionId: number,
  fieldSectionName: string,
  isPrivate: boolean,
): FieldSection[] => {
  return fieldSections.map((section) =>
    section.fieldSectionId === fieldSectionId
      ? { ...section, fieldSectionName, private: isPrivate }
      : section,
  );
};

export const useUpdateFieldSectionMutationV2 = (
  fieldSectionType: FieldSectionType,
  fieldSectionSubType?: FieldSectionSubType,
): UseMutationResult<FieldSection, unknown, UpdateFieldSectionPayload> => {
  const queryClient = useQueryClient();
  const QUERY_KEY = getFieldSectionQueryKey(
    fieldSectionType,
    fieldSectionSubType,
  );

  return useMutation<FieldSection, unknown, UpdateFieldSectionPayload>(
    (fieldSectionPayload) =>
      FieldSectionApi.updateFieldSection(fieldSectionPayload),
    {
      onSuccess: (
        data,
        { fieldSectionId, fieldSectionName, private: isPrivate },
      ) => {
        if (QUERY_KEY) {
          const previous = queryClient.getQueryData<FieldSection[]>(QUERY_KEY);

          if (previous) {
            const updatedSections = updateFieldSectionV2(
              previous,
              fieldSectionId,
              fieldSectionName,
              isPrivate ?? false,
            );

            queryClient.setQueryData(QUERY_KEY, updatedSections);
          }
        }
      },
    },
  );
};
